import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import CreateWidgetModal from '../../widgets/CreateWidgetModel';
import TableWidget from '../../chart-table/TableWidget';

/** Custom markdown components with Tailwind styling for the chatbot bubble */
const markdownComponents = {
  // Paragraphs
  p: ({ children }) => <p className="text-sm mb-2 last:mb-0">{children}</p>,
  // Bold
  strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
  // Italic
  em: ({ children }) => <em className="italic">{children}</em>,
  // Headings
  h1: ({ children }) => <h1 className="text-base font-bold mb-2">{children}</h1>,
  h2: ({ children }) => <h2 className="text-sm font-bold mb-2">{children}</h2>,
  h3: ({ children }) => <h3 className="text-sm font-semibold mb-1">{children}</h3>,
  // Lists
  ul: ({ children }) => <ul className="list-disc list-inside text-sm space-y-0.5 mb-2">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal list-inside text-sm space-y-0.5 mb-2">{children}</ol>,
  li: ({ children }) => <li className="text-sm">{children}</li>,
  // Table
  table: ({ children }) => (
    <div className="overflow-x-auto my-2 rounded-lg border border-gray-200">
      <table className="min-w-full text-xs">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className="bg-gray-200 text-gray-700">{children}</thead>,
  tbody: ({ children }) => <tbody className="divide-y divide-gray-100">{children}</tbody>,
  tr: ({ children }) => <tr className="even:bg-gray-50">{children}</tr>,
  th: ({ children }) => (
    <th className="px-3 py-1.5 text-left text-xs font-semibold whitespace-nowrap">{children}</th>
  ),
  td: ({ children }) => (
    <td className="px-3 py-1.5 whitespace-nowrap">{children}</td>
  ),
  // Code
  code: ({ children }) => (
    <code className="bg-gray-200 text-gray-800 px-1 py-0.5 rounded text-xs">{children}</code>
  ),
  // Horizontal rule
  hr: () => <hr className="my-2 border-gray-300" />,
};

const CashbotMessage = ({ message, context = 'admin', canvasId = null, canvasName = '' }) => {
  const [showWidgetModal, setShowWidgetModal] = useState(false);
  const [showDataModal, setShowDataModal] = useState(false);

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const hasMarkdownTable = (text = '') => /\n\|.*\|\n\|[-:\s|]+\|/m.test(text);

  const renderMessageContent = () => {
    if (message.isError) {
      return (
        <div className="bg-red-50 border border-red-200 rounded p-3 text-red-700">
          <div className="flex items-center space-x-2">
            <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L3.314 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <span className="text-sm font-medium">Error</span>
          </div>
          <p className="mt-1 text-sm">{message.content}</p>
        </div>
      );
    }

    const content = message.content || '';
    const shouldRenderDataTable = message?.data?.table && !hasMarkdownTable(content);

    return (
      <div className="space-y-3">
        {message.type === 'bot' ? (
          <div className="text-sm leading-relaxed">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                p: ({ node, ...props }) => <p className="mb-2 last:mb-0" {...props} />,
                strong: ({ node, ...props }) => <strong className="font-semibold" {...props} />,
                em: ({ node, ...props }) => <em className="italic" {...props} />,
                ul: ({ node, ...props }) => <ul className="list-disc ml-5 mb-2" {...props} />,
                ol: ({ node, ...props }) => <ol className="list-decimal ml-5 mb-2" {...props} />,
                li: ({ node, ...props }) => <li className="mb-1" {...props} />,
                table: ({ node, ...props }) => (
                  <div className="overflow-x-auto my-2">
                    <table className="min-w-full divide-y divide-gray-200 text-sm" {...props} />
                  </div>
                ),
                thead: ({ node, ...props }) => <thead className="bg-gray-50" {...props} />,
                tbody: ({ node, ...props }) => <tbody className="bg-white divide-y divide-gray-200" {...props} />,
                tr: ({ node, ...props }) => <tr className="odd:bg-white even:bg-gray-50" {...props} />,
                th: ({ node, ...props }) => (
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" {...props} />
                ),
                td: ({ node, ...props }) => <td className="px-3 py-2 whitespace-nowrap text-gray-900" {...props} />,
                code: ({ node, ...props }) => (
                  <code className="bg-gray-200 rounded px-1 py-0.5 text-xs" {...props} />
                ),
              }}
            >
              {content}
            </ReactMarkdown>
          </div>
        ) : (
          <p className="text-sm whitespace-pre-wrap">{content}</p>
        )}

        {shouldRenderDataTable && (
          <div className="mt-2">
            <TableWidget table={message.data.table} />
          </div>
        )}

        {/* Create Widget & View Data Buttons */}
        {message.canCreateWidget && (
          <div className="flex gap-2">
            <button
              onClick={() => setShowWidgetModal(true)}
              className="inline-flex items-center px-3 py-1.5 bg-btn-primary text-white text-xs font-medium rounded-lg hover:bg-btn-primary-hover transition-colors"
            >
              <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Create Widget
            </button>
            <button
              onClick={() => setShowDataModal(true)}
              className="inline-flex items-center px-3 py-1.5 bg-btn-primary text-white text-xs font-medium rounded-lg hover:bg-btn-primary-hover transition-colors"
            >
              <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              View Data
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <div className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}>
        <div className={`max-w-[80%] ${message.type === 'user' ? 'order-2' : 'order-1'}`}>
          <div
            className={`rounded-lg px-3 py-2 ${
              message.type === 'user'
                ? 'bg-btn-primary text-white'
                : 'bg-gray-100 text-gray-800'
            }`}
          >
            {renderMessageContent()}
          </div>
          <div className={`text-xs text-gray-400 mt-1 ${message.type === 'user' ? 'text-right' : 'text-left'}`}>
            {formatTimestamp(message.timestamp)}
          </div>
        </div>
        
        {message.type === 'bot' && (
          <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mr-2 order-0">
            <svg className="w-3 h-3 text-btn-primary" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
            </svg>
          </div>
        )}
      </div>

      {/* Create Widget Modal */}
      {showWidgetModal && (
        <CreateWidgetModal
          isOpen={showWidgetModal}
          onClose={() => setShowWidgetModal(false)}
          message={message}
          context={context}
          canvasId={canvasId || message.canvasId}
          canvasName={canvasName}
        />
      )}

      {/* View Data Modal */}
      {showDataModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-lg shadow-lg max-w-lg w-full p-6 relative">
            <button
              onClick={() => setShowDataModal(false)}
              className="absolute top-2 right-2 text-gray-700 hover:text-btn-primary"
              aria-label="Close"
              style={{ fontSize: '2rem', lineHeight: '1' }}
            >
              &times;
            </button>
            <h3 className="text-lg font-semibold mb-2">Widget Data</h3>
            <pre className="bg-gray-100 rounded p-3 text-xs text-gray-700 overflow-x-auto max-h-[400px]">
              {JSON.stringify(message.data, null, 2)}
            </pre>
          </div>
        </div>
      )}
    </>
  );
};

export default CashbotMessage;